import React, { useEffect } from "react";
import PageChrome, { BuyButton, PriceTag, INK, LIME, BLUE, BG_ALT } from "./components/PageChrome";
import { PRODUCTS } from "./products";

const EBOOK = PRODUCTS.ebook_intangibles;
const COURSE = PRODUCTS.course_intangibles;

const EBOOK_CHAPTERS = [
  ["01", "Por qué el banco no ve tus intangibles", "Qué son los activos intangibles y por qué la banca tradicional los ignora — aunque sean tu mayor valor."],
  ["02", "El inventario de lo intangible", "Marca, IP, patentes, software, datos, contratos, cartera recurrente (ARR), licencias y goodwill. Cómo mapearlos."],
  ["03", "Los métodos de valuación", "Costo, mercado e ingresos (DCF), más relief-from-royalty. Cuándo usar cada uno sin humo ni teoría de libro."],
  ["04", "De activo a colateral", "Qué acepta de verdad un banco o fondo como garantía — y qué descarta de inmediato."],
  ["05", "Estructurar deuda contra intangibles", "Borrowing base, advance rates y covenants: cómo se arma la línea alrededor del activo."],
  ["06", "El data room del intangible", "Los documentos que piden y cómo presentarlos para que el deal no se caiga."],
  ["07", "Errores que matan el deal", "Los tropiezos clásicos de valuación y estructura, y cómo evitarlos antes de sentarte a negociar."],
  ["08", "Caso real: fintech + cartera → línea", "Un ejemplo de punta a punta: de la valuación de la cartera recurrente al desembolso."],
];

const COURSE_MODULES = [
  ["Módulo 1", "Fundamentos", "Taxonomía de intangibles y su relevancia crediticia. Qué mueve la aguja para un prestamista."],
  ["Módulo 2", "Métodos aplicados", "Costo / mercado / ingresos / relief-from-royalty, con plantilla de valuación en Excel."],
  ["Módulo 3", "Cartera recurrente y ARR", "Cómo valuar ingresos recurrentes y convertirlos en colateral elegible."],
  ["Módulo 4", "IP, marca y patentes", "Valuación de propiedad intelectual y cómo se gravan a favor del prestamista."],
  ["Módulo 5", "De valuación a estructura", "Borrowing base, advance rate y covenants construidos alrededor del intangible."],
  ["Módulo 6", "Data room y memo", "El memo de valuación y el data room listos para mandar, con plantillas."],
  ["Módulo 7", "Negociación y cierre", "Cómo defender tu valuación frente al prestamista y llegar al desembolso."],
  ["Módulo 8", "Casos reales", "Fintech, SaaS y manufactura con marca — el proceso completo, deal por deal."],
];

function Card({ product, kind }) {
  const isEbook = kind === "ebook";
  return (
    <div className="cg-card" data-testid={`intangibles-${kind}-card`} style={{ padding: 30, display: "flex", flexDirection: "column" }}>
      <div className="cg-mono cg-eyebrow" style={{ fontSize: 10 }}>{isEbook ? "EBOOK · LECTURA" : "CURSO · SELF-SERVE + PLANTILLAS"}</div>
      <div className="cg-h3" style={{ marginTop: 12, fontSize: 22 }}>{product.name.split("·")[1] ? product.name.split("·")[1].trim() : product.name}</div>
      <p style={{ marginTop: 10, opacity: 0.78, lineHeight: 1.55, fontSize: 14.5, flex: 1 }}>{product.blurb}</p>
      {product.features && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {product.features.map((f, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.4 }}>
              <span style={{ color: BLUE, fontWeight: 900 }}>—</span><span>{f}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 18 }}><PriceTag product={product} style={{ marginBottom: 4 }} /></div>
      <div style={{ marginTop: 14 }}>
        <BuyButton lookupKey={product.key} testid={`intangibles-buy-${product.key}`} className={isEbook ? "cg-btn-blue" : ""} style={{ width: "100%", color: isEbook ? "#FFFFFF" : INK }}>
          {isEbook ? "Comprar ebook →" : "Comprar curso →"}
        </BuyButton>
      </div>
    </div>
  );
}

export default function Intangibles() {
  useEffect(() => { document.title = "Valuación de Intangibles · The Debt Guy"; }, []);
  return (
    <PageChrome active="/intangibles" testid="intangibles-page">
      {/* Hero */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 40px 30px" }} data-testid="intangibles-hero">
        <div className="cg-mono cg-eyebrow">— Vertical especializado · Lenders y PyMEs</div>
        <h1 className="cg-h1" style={{ marginTop: 18, maxWidth: 900 }} data-testid="intangibles-hero-title">
          Valúa tus intangibles y <span style={{ color: BLUE }}>levanta deuda contra ellos.</span>
        </h1>
        <p style={{ marginTop: 22, fontSize: 19, lineHeight: 1.55, opacity: 0.8, maxWidth: 700 }}>
          Marca, IP, datos, contratos y cartera recurrente son activos reales — pero el banco no sabe leerlos.
          Aquí aprendes a valuarlos y a convertirlos en colateral que un prestamista sí acepta.
        </p>
        <div style={{ marginTop: 26, display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: BLUE }} />
            <span className="cg-mono" style={{ fontSize: 11, letterSpacing: "0.06em" }}>Para lenders / fintechs</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: LIME, border: `1px solid ${INK}` }} />
            <span className="cg-mono" style={{ fontSize: 11, letterSpacing: "0.06em" }}>Para PyMEs con activos que el banco ignora</span>
          </div>
        </div>
      </section>

      {/* Product cards */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 40px 20px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }}>
          <Card product={EBOOK} kind="ebook" />
          <Card product={COURSE} kind="course" />
        </div>
      </section>

      {/* Ebook outline */}
      <section style={{ background: BG_ALT, borderTop: "1px solid rgba(20,20,20,0.07)", padding: "56px 40px" }} data-testid="intangibles-ebook-outline">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="cg-mono cg-eyebrow">— Qué trae el ebook · {EBOOK.usd}</div>
          <h2 className="cg-h2" style={{ marginTop: 12 }}>8 capítulos, directo al grano</h2>
          <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 }}>
            {EBOOK_CHAPTERS.map(([n, t, d]) => (
              <div key={n} className="cg-card" style={{ padding: 22 }}>
                <div className="cg-mono" style={{ color: BLUE, fontSize: 12, fontWeight: 800 }}>{n}</div>
                <div style={{ marginTop: 8, fontWeight: 800, fontSize: 16, letterSpacing: "-0.01em" }}>{t}</div>
                <p style={{ marginTop: 8, opacity: 0.72, lineHeight: 1.5, fontSize: 13.5 }}>{d}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24 }}>
            <BuyButton lookupKey={EBOOK.key} testid="intangibles-buy-ebook-outline" className="cg-btn-blue" style={{ padding: "16px 28px", fontSize: 13, color: "#FFFFFF" }}>
              Comprar ebook · {EBOOK.usd} →
            </BuyButton>
          </div>
        </div>
      </section>

      {/* Course syllabus */}
      <section style={{ padding: "56px 40px" }} data-testid="intangibles-course-syllabus">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="cg-mono cg-eyebrow">— Temario del curso · {COURSE.usd}</div>
          <h2 className="cg-h2" style={{ marginTop: 12 }}>8 módulos aplicados + plantillas</h2>
          <p style={{ marginTop: 14, fontSize: 16, lineHeight: 1.55, opacity: 0.78, maxWidth: 700 }}>
            Video-lecciones, plantilla de valuación en Excel, checklist de data room y memo template. Del intangible al desembolso.
          </p>
          <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 10 }}>
            {COURSE_MODULES.map(([m, t, d]) => (
              <div key={m} className="cg-card" style={{ padding: "20px 24px", display: "grid", gridTemplateColumns: "140px 1fr", gap: 20, alignItems: "start" }}>
                <div className="cg-mono" style={{ color: BLUE, fontSize: 12, fontWeight: 800, letterSpacing: "0.06em" }}>{m}</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16.5, letterSpacing: "-0.01em" }}>{t}</div>
                  <p style={{ marginTop: 6, opacity: 0.74, lineHeight: 1.5, fontSize: 14 }}>{d}</p>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 26, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <BuyButton lookupKey={COURSE.key} testid="intangibles-buy-course-syllabus" style={{ padding: "16px 28px", fontSize: 13 }}>
              Comprar curso · {COURSE.usd} →
            </BuyButton>
            <BuyButton lookupKey={EBOOK.key} testid="intangibles-buy-ebook-alt" className="cg-btn-blue" style={{ padding: "16px 28px", fontSize: 13, color: "#FFFFFF" }}>
              Empezar con el ebook · {EBOOK.usd}
            </BuyButton>
          </div>
        </div>
      </section>
    </PageChrome>
  );
}
